import React, { useMemo } from 'react';
import {
  useAppStateCurrentAssetReportStore,
  useAppStateManageAssetReportModalDataStore,
} from '../../../common/store/AppStateStore.zustand';
import { 
  getAssetInvestmentTotalAmount, 
  getAssetInvestmentItemList 
} from '../util/AssetStatusCard.util';
import { numberWithCommas, percentageString } from '../../../common/util/NumberUtils';

const InvestmentPerformanceCard = () => {
  const { currentAssetReport } = useAppStateCurrentAssetReportStore();
  const { setManageAssetReportModalData } = useAppStateManageAssetReportModalDataStore();

  const investmentData = useMemo(() => {
    if (!currentAssetReport) return null;
    
    const investmentItems = getAssetInvestmentItemList(currentAssetReport.reportItemList);
    const totalInvestmentAmount = getAssetInvestmentTotalAmount(currentAssetReport.reportItemList);
    const totalPrincipalAmount = investmentItems.reduce((sum, item) => sum + item.principalAmount, 0);
    const totalReturnAmount = totalInvestmentAmount - totalPrincipalAmount;
    const totalReturnRate = totalPrincipalAmount > 0 ? (totalReturnAmount / totalPrincipalAmount) * 100 : 0;

    return {
      totalInvestmentAmount,
      totalPrincipalAmount,
      totalReturnAmount,
      totalReturnRate,
    };
  }, [currentAssetReport]);

  const handleAssetReportClick = () => {
    setManageAssetReportModalData({ isOpen: true, onClose: () => setManageAssetReportModalData(undefined) });
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 hover:shadow-md transition-all duration-300 group relative overflow-hidden">
      <div className="absolute top-0 right-0 w-64 h-64 bg-purple-600/5 rounded-full -translate-y-32 translate-x-32 group-hover:translate-y-[-8rem] transition-transform duration-500"></div>
      <div className="absolute bottom-0 left-0 w-64 h-64 bg-violet-600/5 rounded-full translate-y-32 -translate-x-32 group-hover:translate-y-[8rem] transition-transform duration-500"></div>
      
      <div className="flex justify-between items-center mb-6 relative">
        <div>
          <h3 className="text-xl font-semibold text-gray-800 mb-1 flex items-center">
            <span>투자 성과</span>
          </h3>
          <p className="text-sm text-gray-500">투자 원금, 현재가치 및 수익률 현황</p>
        </div>
      </div>

      {!investmentData || investmentData.totalInvestmentAmount === 0 ? (
        <div className="flex flex-col items-center justify-center h-64 bg-gray-50/50 rounded-lg p-6">
          <div className="w-16 h-16 bg-purple-50 rounded-full flex items-center justify-center mb-4">
            <i className="fas fa-chart-line text-3xl text-purple-300"></i>
          </div>
          <h4 className="text-lg font-medium text-gray-700 mb-2">투자 데이터가 없습니다</h4>
          <p className="text-sm text-gray-500 text-center max-w-sm">
            투자 자산을 추가하여 투자 성과를 확인해보세요.
          </p>
          <button
            className="mt-6 bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-all duration-200 flex items-center space-x-2 !rounded-button whitespace-nowrap"
            onClick={handleAssetReportClick}
          >
            <i className="fas fa-plus"></i>
            <span>투자 자산 추가하기</span>
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {/* 총 투자금액 */}
          <div className="bg-gradient-to-r from-purple-50 to-violet-50 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <span className="text-sm text-gray-600 font-medium">총 투자금액</span>
                <div className="text-2xl font-bold text-gray-900 mt-1">
                  {numberWithCommas(investmentData.totalInvestmentAmount)}원
                </div>
              </div>
              <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                <i className="fas fa-coins text-purple-600 text-xl"></i>
              </div>
            </div>
          </div>

          {/* 투자 원금과 수익 */}
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-blue-50 rounded-lg p-4">
              <div className="flex items-center space-x-2 mb-2">
                <i className="fas fa-piggy-bank text-blue-600"></i>
                <span className="text-sm text-blue-700 font-medium">투자 원금</span>
              </div>
              <div className="text-xl font-bold text-blue-900">
                {numberWithCommas(investmentData.totalPrincipalAmount)}원
              </div>
            </div>

            <div className={`rounded-lg p-4 ${
              investmentData.totalReturnAmount >= 0 ? 'bg-green-50' : 'bg-red-50'
            }`}>
              <div className="flex items-center space-x-2 mb-2">
                <i className={`fas ${
                  investmentData.totalReturnAmount >= 0 ? 'fa-arrow-up text-green-600' : 'fa-arrow-down text-red-600'
                }`}></i>
                <span className={`text-sm font-medium ${
                  investmentData.totalReturnAmount >= 0 ? 'text-green-700' : 'text-red-700'
                }`}>
                  투자 수익
                </span>
              </div>
              <div className={`text-xl font-bold ${
                investmentData.totalReturnAmount >= 0 ? 'text-green-900' : 'text-red-900'
              }`}>
                {investmentData.totalReturnAmount >= 0 ? '+' : ''}
                {numberWithCommas(investmentData.totalReturnAmount)}원
              </div>
            </div>
          </div>

          {/* 투자 수익률 */}
          <div className={`rounded-lg p-4 ${
            investmentData.totalReturnRate >= 0 ? 'bg-emerald-50' : 'bg-orange-50'
          }`}>
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center space-x-2 mb-1">
                  <i className={`fas fa-percentage ${
                    investmentData.totalReturnRate >= 0 ? 'text-emerald-600' : 'text-orange-600'
                  }`}></i>
                  <span className={`text-sm font-medium ${
                    investmentData.totalReturnRate >= 0 ? 'text-emerald-700' : 'text-orange-700'
                  }`}>
                    투자 수익률
                  </span>
                </div>
                <div className={`text-2xl font-bold ${
                  investmentData.totalReturnRate >= 0 ? 'text-emerald-900' : 'text-orange-900'
                }`}>
                  {investmentData.totalReturnRate >= 0 ? '+' : ''}
                  {percentageString(investmentData.totalReturnRate)}
                </div>
              </div>
              <div className={`w-16 h-16 rounded-full flex items-center justify-center ${
                investmentData.totalReturnRate >= 0 ? 'bg-emerald-100' : 'bg-orange-100'
              }`}>
                <i className={`fas fa-chart-line text-2xl ${
                  investmentData.totalReturnRate >= 0 ? 'text-emerald-600' : 'text-orange-600'
                }`}></i>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InvestmentPerformanceCard;